/**
 * turnRefusal.ts — the closed set of server refusals whose OWN words reach the
 * person (2026-09-12, flipping ops/learningPlane.workspaceGate to 'enforce').
 *
 * WHY THIS EXISTS. `prest0nRunTurn` can refuse a turn with a typed 409 carrying
 * copy written for the reader ("This conversation belongs to a different
 * workspace. Switch to that workspace to continue this thread."). The client
 * threw that away: every non-OK response became one generic Error, and neither
 * WORKSPACE_MISMATCH nor THREAD_SESSION_MISMATCH appeared anywhere in src/. So
 * under 'enforce' the reader would have got "Prest0n couldn't start this reply.
 * Tap retry" — a dead end, and a promise the product cannot keep, because
 * retrying from the same tab fails identically.
 *
 * That is the INCIDENT-20260909 shape: a generic card sent a user signing out,
 * opening fresh threads and suspecting unrelated work, when one honest sentence
 * would have told her what to do.
 *
 * WHY A CLOSED ALLOWLIST, not "show whatever the server said". Only classes we
 * have deliberately written copy for get to speak; every other failure keeps
 * the generic card and its trace id BYTE-IDENTICAL, because those ARE bugs and
 * the ref is the point (platformIncidents.userFacingTurnFailureCopy doctrine,
 * 2026-09-09). An open passthrough would turn any upstream 4xx body into chat
 * text, which is how a stack trace ends up in front of a customer.
 *
 * Leaf module: no imports, so both the transport and the chat surface can use
 * it without dragging either graph into the other.
 */

/** Server codes whose `message` is written FOR THE READER and may be shown verbatim. */
export const USER_FACING_REFUSAL_CODES = [
  // The thread belongs to another workspace than the one this tab is acting in.
  'WORKSPACE_MISMATCH',
  // The session named does not belong to the thread named.
  'THREAD_SESSION_MISMATCH',
] as const;

export type TurnRefusalCode = (typeof USER_FACING_REFUSAL_CODES)[number];

export interface TurnRefusal {
  code: TurnRefusalCode;
  /** The server's own sentence — already written for the reader. */
  message: string;
}

/** Longest sentence we will render verbatim. Beyond this something is wrong with the sender. */
const MAX_REFUSAL_MESSAGE_LENGTH = 300;

function isUserFacingCode(code: unknown): code is TurnRefusalCode {
  return (
    typeof code === 'string'
    && (USER_FACING_REFUSAL_CODES as readonly string[]).includes(code)
  );
}

/**
 * Read a refusal out of a non-OK response body, or null.
 *
 * Null is the fail-open answer for EVERYTHING unrecognised — an unparseable
 * body, an unknown code, a blank or oversized message — and null means the
 * caller behaves exactly as it did before this module existed.
 */
export function parseTurnRefusal(rawBody: string | null | undefined): TurnRefusal | null {
  const raw = String(rawBody ?? '').trim();
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  const error = (parsed as { error?: unknown } | null)?.error;
  if (!error || typeof error !== 'object') return null;
  const { code, message } = error as { code?: unknown; message?: unknown };
  if (!isUserFacingCode(code)) return null;
  const text = typeof message === 'string' ? message.trim() : '';
  if (!text || text.length > MAX_REFUSAL_MESSAGE_LENGTH) return null;
  return { code, message: text };
}

/**
 * An Error that carries a refusal the reader should see.
 *
 * It stays an Error so every existing catch, log and retry path is unchanged —
 * the refusal rides ALONGSIDE the diagnostic message, never instead of it, so
 * the browser console and orchestratorDecisionLog keep the full status line.
 */
export class TurnRefusedError extends Error {
  readonly refusal: TurnRefusal;

  constructor(diagnosticMessage: string, refusal: TurnRefusal) {
    super(diagnosticMessage);
    this.name = 'TurnRefusedError';
    this.refusal = refusal;
    // Subclassing a built-in under an ES5 target breaks the prototype chain,
    // so `instanceof` returns false for a genuine instance. Caught by the test
    // before this shipped: without the restore, the refusal would never be
    // recognised and every reader would still have met the generic card — the
    // whole change silently inert in production while the unit tests that did
    // not use instanceof all passed.
    Object.setPrototypeOf(this, TurnRefusedError.prototype);
  }
}

/**
 * The refusal carried by an error, or null for every other failure.
 *
 * Reads the BRAND rather than `instanceof`: prototype identity is the fragile
 * thing here (transpilation target, a duplicated module instance across
 * bundles), and a refusal that fails to be recognised degrades silently to the
 * generic card — the exact failure this module exists to prevent.
 */
export function refusalFromError(err: unknown): TurnRefusal | null {
  const candidate = (err as { refusal?: unknown } | null | undefined)?.refusal;
  if (!candidate || typeof candidate !== 'object') return null;
  const { code, message } = candidate as { code?: unknown; message?: unknown };
  if (!isUserFacingCode(code)) return null;
  const text = typeof message === 'string' ? message.trim() : '';
  if (!text || text.length > MAX_REFUSAL_MESSAGE_LENGTH) return null;
  return { code, message: text };
}
